/**
 * Candlestick + volume panel for one stock (TradingView Lightweight Charts, Phase 1).
 *
 * DATA SOURCE: TekInvest's own authorized API only --
 *   GET /api/v1/stocks/scanner/{symbol}/history?months=N  (via `stockMarketService.getStockHistory`)
 * That endpoint returns the daily OHLCV bars imported from ICICI Breeze, plus the indicator
 * series (kept in the response so a later phase can overlay EMAs without a new request).
 *
 * INTERVAL: DAILY ONLY. The platform stores no intraday stock bars and has no live quote
 * stream for stocks, so this panel offers a date RANGE selector (3M-5Y), not a timeframe
 * selector, and labels the data "end-of-day". It does not claim real-time prices.
 *
 * `initialHistory` lets a parent that has ALREADY loaded the default-range history (the Stock
 * Data detail dialog) share it, so opening the dialog does not issue a duplicate request.
 */
import { useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, CircularProgress, Stack, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import stockMarketService from "@/services/stockMarketService";
import type { StockHistoryResponse } from "@/types";
import { extractErrorMessage } from "@/utils/errors";
import CandlestickVolumeChart from "./CandlestickVolumeChart";
import {
  EMA_OVERLAY_DEFS,
  assessFreshness,
  indicatorToLineOverlay,
  lastCandleDate,
  toCandlestickChartData,
  type EmaId,
} from "./candlestickChartData";

/** Date ranges offered; the backend accepts 1-60 months. */
const RANGE_OPTIONS: ReadonlyArray<{ months: number; label: string }> = [
  { months: 3, label: "3M" },
  { months: 6, label: "6M" },
  { months: 12, label: "1Y" },
  { months: 36, label: "3Y" },
  { months: 60, label: "5Y" },
];

export interface StockCandlestickPanelProps {
  symbol: string;
  /** History already fetched for `initialMonths`; avoids a duplicate request for that range. */
  initialHistory?: StockHistoryResponse | null;
  initialMonths?: number;
  height?: number;
}

type LoadState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; history: StockHistoryResponse };

export default function StockCandlestickPanel({
  symbol,
  initialHistory = null,
  initialMonths = 6,
  height,
}: StockCandlestickPanelProps) {
  const [months, setMonths] = useState(initialMonths);
  const [reloadToken, setReloadToken] = useState(0);
  // User-controlled EMA overlays; all off by default so the chart opens exactly as in Phase 1.
  const [enabledEmas, setEnabledEmas] = useState<EmaId[]>([]);
  const [state, setState] = useState<LoadState>(
    initialHistory ? { status: "ready", history: initialHistory } : { status: "loading" }
  );

  useEffect(() => {
    // Reuse the parent's already-loaded history for the default range (no duplicate request).
    if (initialHistory && months === initialMonths && reloadToken === 0) {
      setState({ status: "ready", history: initialHistory });
      return undefined;
    }
    let cancelled = false;
    setState({ status: "loading" });
    stockMarketService
      .getStockHistory(symbol, months)
      .then((history) => {
        if (!cancelled) setState({ status: "ready", history });
      })
      .catch((err: unknown) => {
        if (!cancelled) setState({ status: "error", message: extractErrorMessage(err) });
      });
    return () => {
      cancelled = true;
    };
  }, [symbol, months, reloadToken, initialHistory, initialMonths]);

  const chartData = useMemo(
    () => (state.status === "ready" ? toCandlestickChartData(state.history.prices) : null),
    [state]
  );

  // EMA lines come from the SAME history response (no extra request). Built once per response,
  // so their data arrays keep a stable identity while the user toggles individual lines.
  const allOverlays = useMemo(
    () =>
      state.status === "ready"
        ? EMA_OVERLAY_DEFS.map((def) => indicatorToLineOverlay(state.history.indicators, def.field, def))
        : [],
    [state]
  );
  const availableEmaIds = useMemo(
    () => new Set<EmaId>(allOverlays.filter((o) => o.data.length > 0).map((o) => o.id as EmaId)),
    [allOverlays]
  );
  // Only lines that are switched on AND have data are drawn; identity changes only when that set does.
  const visibleOverlays = useMemo(
    () => allOverlays.filter((o) => enabledEmas.includes(o.id as EmaId) && o.data.length > 0),
    [allOverlays, enabledEmas]
  );
  const unavailableSelected = EMA_OVERLAY_DEFS.filter(
    (d) => enabledEmas.includes(d.id) && state.status === "ready" && !availableEmaIds.has(d.id)
  );

  const lastDate = chartData ? lastCandleDate(chartData.candles) : null;
  const freshness = useMemo(() => assessFreshness(lastDate), [lastDate]);

  return (
    <Stack spacing={1.5} data-testid="stock-candlestick-panel">
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" rowGap={1}>
        <Typography variant="subtitle2" fontWeight={700}>
          Candlestick &amp; Volume (daily)
        </Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={months}
          onChange={(_event, value: number | null) => {
            if (value !== null) setMonths(value);
          }}
          aria-label="Chart date range"
        >
          {RANGE_OPTIONS.map((option) => (
            <ToggleButton key={option.months} value={option.months} aria-label={`${option.label} range`}>
              {option.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>

      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" rowGap={1}>
        <Typography variant="caption" color="text.secondary">
          Overlays
        </Typography>
        <ToggleButtonGroup
          size="small"
          value={enabledEmas.filter((id) => availableEmaIds.has(id))}
          onChange={(_event, value: EmaId[]) =>
            // Keep a selection the current range has no data for, so it returns with the data.
            setEnabledEmas(Array.from(new Set([...value, ...enabledEmas.filter((id) => !availableEmaIds.has(id))])))
          }
          aria-label="Indicator overlays"
        >
          {EMA_OVERLAY_DEFS.map((def) => (
            <ToggleButton
              key={def.id}
              value={def.id}
              aria-label={`${def.label} overlay`}
              disabled={state.status !== "ready" || !availableEmaIds.has(def.id)}
              sx={{ borderLeft: `3px solid ${def.color}` }}
            >
              {def.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>

      {state.status === "loading" && (
        <Box display="flex" justifyContent="center" py={6} role="status" aria-label="Loading chart">
          <CircularProgress />
        </Box>
      )}

      {state.status === "error" && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => setReloadToken((n) => n + 1)}>
              Retry
            </Button>
          }
        >
          {state.message}
        </Alert>
      )}

      {chartData && chartData.candles.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No daily price history for {symbol} yet — an admin needs to import stock data first.
        </Typography>
      )}

      {chartData && chartData.candles.length > 0 && (
        <>
          {freshness.status === "stale" && (
            <Alert severity="warning" data-testid="stale-data-warning">
              Price data may be stale: the last daily bar is {freshness.lastBarDate}, with {freshness.missedWeekdays}{" "}
              weekdays since then before today ({freshness.today}, IST). These are not current prices; an admin may need
              to re-import stock data.
            </Alert>
          )}
          <CandlestickVolumeChart
            candles={chartData.candles}
            volume={chartData.volume}
            overlays={visibleOverlays}
            height={height}
          />
          <Typography variant="caption" color="text.secondary">
            End-of-day candles, last bar {lastDate} · daily interval only (no intraday or live data for stocks)
            {chartData.skippedRows > 0 ? ` · ${chartData.skippedRows} invalid row(s) skipped` : ""}
            {unavailableSelected.length > 0
              ? ` · No ${unavailableSelected.map((d) => d.label).join(", ")} data for this range`
              : ""}
          </Typography>
        </>
      )}
    </Stack>
  );
}
